import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import SelectField from './SelectField';
import projectService from '../../../services/projectService';
import { useNotification } from '../../../contexts/NotificationContext';

/**
 * A specialized Select dropdown for selecting a Project/Site.
 * Automatically fetches available projects and includes a "Platform (Global)" option.
 */
const ProjectSelector = ({
    label = 'Project / Site',
    name = 'projectId',
    includeGlobal = true,
    globalLabel = 'Platform (Global)',
    includeAll = false,
    allLabel = 'All Projects / Sites',
    onChange,
    value,
    ...props
}) => {
    const [projects, setProjects] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const { addNotification } = useNotification();

    useEffect(() => {
        const fetchProjects = async () => {
            setIsLoading(true);
            try {
                // Fetch all projects (admin/editor access)
                const response = await projectService.getAllProjects({ limit: 100 });
                if (response.data && response.data.rows) {
                    setProjects(response.data.rows);
                }
            } catch (error) {
                console.error('Failed to fetch projects for selector:', error);
                addNotification('Failed to load projects list', 'error');
            } finally {
                setIsLoading(false);
            }
        };

        fetchProjects();
    }, [addNotification]);

    const options = [];
    if (includeAll) {
        options.push({ value: '', label: allLabel });
    }
    if (includeGlobal) {
        options.push({ value: 0, label: globalLabel });
    }

    projects.forEach(project => {
        options.push({
            value: project.id,
            label: project.name || `Project #${project.id}`
        });
    });

    return (
        <SelectField
            label={label}
            name={name}
            options={options}
            disabled={isLoading}
            placeholder={isLoading ? 'Loading projects...' : props.placeholder}
            value={value}
            onChange={onChange}
            {...props}
        />
    );
};

ProjectSelector.propTypes = {
    label: PropTypes.string,
    name: PropTypes.string,
    includeGlobal: PropTypes.bool,
    globalLabel: PropTypes.string,
    onChange: PropTypes.func,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    placeholder: PropTypes.string,
};

export default ProjectSelector;
